import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import QRCode from "qrcode";
import { Download, Copy, QrCode, Plus, Trash2, Upload, Loader2, Check, Music, Users, AlertTriangle, Printer } from "lucide-react";
import { api, assetUrl, formatApiErrorDetail } from "@/lib/api";
import { compressImage } from "@/lib/compressImage";
import { useAuth } from "@/context/AuthContext";
import { QrCardPrint } from "./PrintViews";

// Default is the upload nudge: printed and displayed AT the wedding (table
// cards, welcome table), getting guests to actually scan during the event
// matters more than the "come scoprire il sito" framing that fits an invite
// mailed out beforehand instead.
const QR_CAPTION_PRESETS = [
  "Carica le tue foto e i tuoi video qui! 📸",
  "Scopri tutto sul nostro matrimonio",
  "Scansiona per il programma e la location",
  "Conferma qui la tua presenza",
];

const DRESS_CODE_SUGGESTIONS = [
  "Elegante",
  "Black tie",
  "Smart casual",
  "Cerimonia all'aperto: evitare tacchi a spillo",
  "Si prega di evitare il bianco",
];

const FAQ_SUGGESTIONS = [
  "È disponibile un parcheggio?",
  "I bambini sono benvenuti?",
  "Posso portare un accompagnatore?",
  "A che ora inizia la cerimonia?",
  "C'è un servizio navetta?",
];

const FONTS = ["Playfair Display", "Cormorant Garamond", "Lora"];
const PRESETS = ["#BFA588", "#8F9779", "#2C3E35", "#A6544C", "#5B6B8C", "#B08BA5"];

// Default stock photos the couple can pick with one click instead of hunting for a
// URL -- a manual link or an upload are still available right below.
const HERO_PRESETS = [
  "https://images.pexels.com/photos/28824204/pexels-photo-28824204.jpeg?auto=compress&w=1200",
  "https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
  "https://picsum.photos/seed/wedding-hero-1/1200/800",
  "https://picsum.photos/seed/wedding-hero-2/1200/800",
  "https://picsum.photos/seed/wedding-hero-3/1200/800",
  "https://picsum.photos/seed/wedding-hero-4/1200/800",
];
const STORY_PRESETS = [
  "https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
  "https://images.pexels.com/photos/28824204/pexels-photo-28824204.jpeg?auto=compress&w=1200",
  "https://picsum.photos/seed/wedding-story-1/1200/800",
  "https://picsum.photos/seed/wedding-story-2/1200/800",
  "https://picsum.photos/seed/wedding-story-3/1200/800",
  "https://picsum.photos/seed/wedding-story-4/1200/800",
];

// yyyy-MM-ddTHH:mm in local time, for the datetime-local `min` guard.
function nowLocalDatetime() {
  const d = new Date();
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function ImagePicker({ presets, value, onChange, kind }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", await compressImage(file, 2500, 0.85));
      fd.append("kind", kind);
      const { data } = await api.post("/owner/wedding/image", fd);
      onChange(data.url);
      toast.success("Immagine caricata");
    } catch {
      toast.error("Errore nel caricamento dell'immagine");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const customPreview = value && !presets.includes(value);

  return (
    <div>
      {customPreview && (
        <div className="mb-3 flex items-center gap-3">
          <img src={assetUrl(value)} alt="" className="w-16 h-16 rounded-lg object-cover border" style={{ borderColor: "#e3ddd2" }} />
          <p className="text-xs text-neutral-400">Immagine attuale (link o caricamento personalizzato)</p>
        </div>
      )}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
        {presets.map((url) => (
          <button
            key={url}
            type="button"
            onClick={() => onChange(url)}
            className="relative aspect-square rounded-lg overflow-hidden border-2 transition-colors duration-150"
            style={{ borderColor: value === url ? "var(--wed-primary)" : "transparent" }}
            data-testid={`${kind}-preset-${url.slice(-6)}`}
          >
            <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
            {value === url && (
              <span className="absolute inset-0 bg-black/20 flex items-center justify-center">
                <Check size={16} className="text-white" />
              </span>
            )}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input className="input-wed flex-1" value={value} onChange={(e) => onChange(e.target.value)} placeholder="…oppure incolla un link" data-testid={`${kind}-url-input`} />
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`${kind}-file-input`} />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="btn-outline-wed !py-2 !px-3 text-sm flex items-center gap-1.5 shrink-0"
          data-testid={`${kind}-upload-button`}
        >
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
          <span className="hidden sm:inline">Carica</span>
        </button>
      </div>
    </div>
  );
}

// A compact on/off pill used wherever a public-page section can be toggled.
function ToggleSwitch({ checked, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="relative w-10 h-6 rounded-full shrink-0 transition-colors duration-150"
      style={{ background: checked ? "var(--wed-primary)" : "#d8d2c4" }}
      data-testid={testId}
    >
      <span
        className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-150"
        style={{ transform: checked ? "translateX(16px)" : "translateX(0)" }}
      />
    </button>
  );
}

// Every card for a toggleable public-page section shares this shell: title +
// switch in the header, and the body dims when off so it's obvious at a
// glance what's live on the site without reading any text.
function SectionCard({ title, enabled, onToggle, testId, children }) {
  return (
    <div className="bg-white border rounded-2xl p-6 space-y-4" style={{ borderColor: "#e3ddd2" }} data-testid={testId}>
      <div className="flex items-center justify-between">
        <h2 className="font-wed text-xl">{title}</h2>
        {onToggle && <ToggleSwitch checked={enabled} onChange={onToggle} testId={`${testId}-toggle`} />}
      </div>
      <div className="space-y-4 transition-opacity duration-150" style={{ opacity: enabled === false ? 0.45 : 1 }}>
        {children}
      </div>
    </div>
  );
}

export default function SettingsTab({ wedding, setWedding }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [qr, setQr] = useState("");
  const [qrCaption, setQrCaption] = useState(QR_CAPTION_PRESETS[0]);
  const [showQrPrint, setShowQrPrint] = useState(false);

  const publicUrl = `${window.location.origin}/${wedding.slug}`;

  useEffect(() => {
    QRCode.toDataURL(publicUrl, { width: 640, margin: 2, color: { dark: "#1a1a1a", light: "#ffffff" } })
      .then(setQr)
      .catch(() => {});
  }, [publicUrl]);

  const downloadQr = () => {
    const a = document.createElement("a");
    a.href = qr;
    a.download = `qr-${wedding.slug}.png`;
    a.click();
  };

  const copyLink = async (link = publicUrl) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(link);
      } else {
        const ta = document.createElement("textarea");
        ta.value = link;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Link copiato!");
    } catch {
      toast.error("Impossibile copiare il link");
    }
  };

  const albumUrl = `${publicUrl}/album`;

  useEffect(() => {
    setForm({
      nome_sposi: wedding.nome_sposi,
      data_evento: wedding.data_evento?.slice(0, 16),
      location: wedding.location,
      location_map_url: wedding.location_map_url || "",
      rsvp_enabled: wedding.rsvp_enabled !== false,
      rsvp_cutoff_days: wedding.rsvp_cutoff_days ?? 7,
      rsvp_ask_moments: !!wedding.rsvp_ask_moments,
      dress_code: wedding.dress_code || "",
      faq: wedding.faq || [],
      alloggi_text: wedding.alloggi_text || "",
      gift_enabled: !!wedding.gift_enabled,
      gift_message: wedding.gift_message || "",
      gift_iban: wedding.gift_iban || "",
      songs_enabled: !!wedding.songs_enabled,
      uploads_force_open: !!wedding.uploads_force_open,
      uploads_open_at: wedding.uploads_open_at || "",
      uploads_close_at: wedding.uploads_close_at || "",
      gallery_published: !!wedding.gallery_published,
      gallery_publish_at: wedding.gallery_publish_at || "",
      story_text: wedding.story_text || "",
      story_photo_url: wedding.story_photo_url || "",
      hero_photo_url: wedding.hero_photo_url || "",
      hero_photo_url_mobile: wedding.hero_photo_url_mobile || "",
      primary: wedding.tema_colore?.primary || "#BFA588",
      font: wedding.tema_colore?.font || "Playfair Display",
      moments: wedding.moments || [],
      sections_enabled: wedding.sections_enabled || {},
      tables_unlock_at: wedding.tables_unlock_at || "",
      tables_hide_at: wedding.tables_hide_at || "",
    });
  }, [wedding]);

  const [songs, setSongs] = useState([]);
  useEffect(() => {
    if (wedding.songs_enabled) api.get("/owner/songs").then((r) => setSongs(r.data)).catch(() => {});
  }, [wedding.songs_enabled]);

  const removeSong = async (id) => {
    try {
      await api.delete(`/owner/songs/${id}`);
      setSongs((s) => s.filter((x) => x.id !== id));
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  if (!form) return null;

  const toggleSection = (key, enabled) =>
    setForm((f) => ({ ...f, sections_enabled: { ...f.sections_enabled, [key]: enabled } }));

  const addFaq = (domanda = "") => setForm((f) => ({ ...f, faq: [...f.faq, { id: crypto.randomUUID(), domanda, risposta: "" }] }));
  const updateFaq = (i, key, value) => setForm((f) => ({ ...f, faq: f.faq.map((item, idx) => (idx === i ? { ...item, [key]: value } : item)) }));
  const removeFaq = (i) => setForm((f) => ({ ...f, faq: f.faq.filter((_, idx) => idx !== i) }));

  const addMoment = () => setForm((f) => ({ ...f, moments: [...f.moments, { id: crypto.randomUUID(), label: "", requiresSeat: true }] }));
  const updateMoment = (i, label) => setForm((f) => ({ ...f, moments: f.moments.map((m, idx) => (idx === i ? { ...m, label } : m)) }));
  const toggleMomentSeat = (i, requiresSeat) => setForm((f) => ({ ...f, moments: f.moments.map((m, idx) => (idx === i ? { ...m, requiresSeat } : m)) }));
  const removeMoment = (i) => setForm((f) => ({ ...f, moments: f.moments.filter((_, idx) => idx !== i) }));

  const minDatetime = nowLocalDatetime();

  const save = async (e) => {
    e.preventDefault();
    if (form.data_evento !== wedding.data_evento?.slice(0, 16) && form.data_evento < minDatetime) {
      return toast.error("La data dell'evento non può essere nel passato");
    }
    setSaving(true);
    try {
      const { data } = await api.put("/owner/wedding", {
        nome_sposi: form.nome_sposi,
        data_evento: form.data_evento,
        location: form.location,
        location_map_url: form.location_map_url,
        rsvp_enabled: form.rsvp_enabled,
        rsvp_cutoff_days: form.rsvp_cutoff_days,
        rsvp_ask_moments: form.rsvp_ask_moments,
        dress_code: form.dress_code,
        faq: form.faq.filter((f) => f.domanda.trim()).map((f) => ({ ...f, domanda: f.domanda.trim(), risposta: f.risposta.trim() })),
        alloggi_text: form.alloggi_text,
        gift_enabled: form.gift_enabled,
        gift_message: form.gift_message,
        gift_iban: form.gift_iban,
        songs_enabled: form.songs_enabled,
        uploads_force_open: form.uploads_force_open,
        uploads_open_at: form.uploads_open_at || null,
        uploads_close_at: form.uploads_close_at || null,
        gallery_published: form.gallery_published,
        gallery_publish_at: form.gallery_publish_at || null,
        story_text: form.story_text,
        story_photo_url: form.story_photo_url,
        hero_photo_url: form.hero_photo_url,
        hero_photo_url_mobile: form.hero_photo_url_mobile,
        tema_colore: { primary: form.primary, font: form.font },
        moments: form.moments.map((m) => ({ ...m, label: m.label.trim() })).filter((m) => m.label),
        sections_enabled: form.sections_enabled,
        tables_unlock_at: form.tables_unlock_at || null,
        tables_hide_at: form.tables_hide_at || null,
      });
      setWedding((w) => ({ ...w, ...data }));
      toast.success("Impostazioni salvate");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-2xl space-y-6" data-testid="settings-tab">
      <div className="bg-white border rounded-2xl p-6 space-y-4" style={{ borderColor: "#e3ddd2" }}>
        <h2 className="font-wed text-xl">Dettagli evento</h2>
        <div>
          <label className="label-wed">Nomi sposi</label>
          <input className="input-wed" value={form.nome_sposi} onChange={(e) => setForm({ ...form, nome_sposi: e.target.value })} data-testid="settings-names-input" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label-wed">Data e ora</label>
            <input className="input-wed" type="datetime-local" min={minDatetime} value={form.data_evento} onChange={(e) => setForm({ ...form, data_evento: e.target.value })} data-testid="settings-date-input" />
          </div>
          <div>
            <label className="label-wed">Location</label>
            <input className="input-wed" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="settings-location-input" />
          </div>
        </div>
        <div>
          <label className="label-wed">Foto hero (desktop)</label>
          <ImagePicker presets={HERO_PRESETS} value={form.hero_photo_url} onChange={(url) => setForm({ ...form, hero_photo_url: url })} kind="hero" />
        </div>
        <div>
          <label className="label-wed">Foto hero (telefono)</label>
          <p className="text-xs text-neutral-400 -mt-1 mb-2">Facoltativa — scegli una foto verticale/centrata per un risultato migliore su schermi piccoli. Se la lasci vuota, su telefono si usa quella sopra.</p>
          <ImagePicker presets={HERO_PRESETS} value={form.hero_photo_url_mobile} onChange={(url) => setForm({ ...form, hero_photo_url_mobile: url })} kind="hero_mobile" />
        </div>
      </div>

      <SectionCard
        title="La nostra storia"
        enabled={form.sections_enabled.story !== false}
        onToggle={(v) => toggleSection("story", v)}
        testId="story-settings-card"
      >
        <textarea className="input-wed" rows={6} value={form.story_text} onChange={(e) => setForm({ ...form, story_text: e.target.value })} placeholder="Come vi siete conosciuti…" data-testid="settings-story-input" />
        <ImagePicker presets={STORY_PRESETS} value={form.story_photo_url} onChange={(url) => setForm({ ...form, story_photo_url: url })} kind="story" />
      </SectionCard>

      <SectionCard title="Mappa &amp; meteo" testId="location-weather-card">
        <div className="flex items-center justify-between">
          <label className="label-wed !mb-0">Mappa della location</label>
          <ToggleSwitch checked={form.sections_enabled.location !== false} onChange={(v) => toggleSection("location", v)} testId="location-toggle" />
        </div>
        <input className="input-wed" value={form.location_map_url} onChange={(e) => setForm({ ...form, location_map_url: e.target.value })} placeholder="Link Google Maps (facoltativo)" data-testid="settings-location-map-input" />
        <div className="flex items-center justify-between pt-3 border-t" style={{ borderColor: "#f0ece1" }}>
          <label className="label-wed !mb-0">Previsioni meteo</label>
          <ToggleSwitch checked={form.sections_enabled.weather !== false} onChange={(v) => toggleSection("weather", v)} testId="weather-toggle" />
        </div>
      </SectionCard>

      <SectionCard
        title="Dress code"
        enabled={form.sections_enabled.dress_code !== false}
        onToggle={(v) => toggleSection("dress_code", v)}
        testId="dress-code-settings-card"
      >
        <div className="flex flex-wrap gap-2">
          {DRESS_CODE_SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setForm((f) => ({ ...f, dress_code: f.dress_code ? `${f.dress_code}\n${s}` : s }))}
              className="text-xs bg-[#faf6ee] border rounded-full px-3 py-1.5 hover:border-[var(--wed-primary)] transition-colors duration-150"
              style={{ borderColor: "#e3ddd2" }}
              data-testid={`dress-code-suggestion-${s.slice(0, 10)}`}
            >
              <Plus size={11} className="inline -mt-0.5 mr-1" />{s}
            </button>
          ))}
        </div>
        <textarea className="input-wed" rows={3} value={form.dress_code} onChange={(e) => setForm({ ...form, dress_code: e.target.value })} placeholder="Es. Elegante, evitare il bianco" data-testid="dress-code-input" />
      </SectionCard>

      <SectionCard
        title="Programma della giornata"
        enabled={form.sections_enabled.program !== false}
        onToggle={(v) => toggleSection("program", v)}
        testId="program-settings-card"
      >
        <p className="text-sm text-neutral-500">Orari e voci si modificano nella scheda "Programma" della dashboard.</p>
      </SectionCard>

      <SectionCard
        title="Alloggi per gli invitati"
        enabled={form.sections_enabled.accommodation !== false}
        onToggle={(v) => toggleSection("accommodation", v)}
        testId="accommodation-settings-card"
      >
        <textarea className="input-wed" rows={4} value={form.alloggi_text} onChange={(e) => setForm({ ...form, alloggi_text: e.target.value })} placeholder="Es. Hotel Rose, a 5 minuti dalla location — tariffa convenzionata" data-testid="accommodation-input" />
      </SectionCard>

      <SectionCard
        title="Galleria foto &amp; video"
        enabled={form.sections_enabled.gallery !== false}
        onToggle={(v) => toggleSection("gallery", v)}
        testId="gallery-settings-card"
      >
        <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer">
          <input type="checkbox" checked={form.uploads_force_open} onChange={(e) => setForm({ ...form, uploads_force_open: e.target.checked })} data-testid="uploads-force-open-toggle" />
          Sblocca subito il caricamento (ignora le date)
        </label>
        <div className="grid sm:grid-cols-2 gap-4" data-testid="uploads-window-card">
          <div>
            <label className="label-wed">Apre dalle</label>
            <input className="input-wed" type="datetime-local" value={form.uploads_open_at} onChange={(e) => setForm({ ...form, uploads_open_at: e.target.value })} data-testid="uploads-open-input" />
            <p className="text-xs text-neutral-400 mt-1">Vuoto = 1 giorno prima.</p>
          </div>
          <div>
            <label className="label-wed">Chiude dalle</label>
            <input className="input-wed" type="datetime-local" value={form.uploads_close_at} onChange={(e) => setForm({ ...form, uploads_close_at: e.target.value })} data-testid="uploads-close-input" />
            <p className="text-xs text-neutral-400 mt-1">Vuoto = 7 giorni dopo.</p>
          </div>
        </div>

        <div className="pt-3 border-t space-y-3" style={{ borderColor: "#f0ece1" }}>
          <p className="text-sm text-neutral-500">Gli ospiti caricano ma non vedono nulla: voi moderate in "Foto", poi pubblicate l'album.</p>
          <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer">
            <input type="checkbox" checked={form.gallery_published} onChange={(e) => setForm({ ...form, gallery_published: e.target.checked })} data-testid="gallery-published-toggle" />
            Pubblica l'album subito
          </label>
          <div className="max-w-xs">
            <label className="label-wed">…oppure programma per</label>
            <input className="input-wed" type="datetime-local" value={form.gallery_publish_at} onChange={(e) => setForm({ ...form, gallery_publish_at: e.target.value })} data-testid="gallery-publish-at-input" />
          </div>
          {form.gallery_published && (
            <div className="bg-[#faf6ee] border rounded-xl p-3 flex items-center gap-2" style={{ borderColor: "#e3ddd2" }} data-testid="album-link-box">
              <p className="text-xs text-neutral-600 break-all flex-1">{albumUrl}</p>
              <button type="button" onClick={() => copyLink(albumUrl)} className="btn-outline-wed !py-1.5 !px-3 text-xs shrink-0" data-testid="album-link-copy-button">
                <Copy size={13} />
              </button>
            </div>
          )}
        </div>
      </SectionCard>

      <SectionCard
        title="Trova il tuo tavolo"
        enabled={form.sections_enabled.table_finder !== false}
        onToggle={(v) => toggleSection("table_finder", v)}
        testId="table-finder-settings-card"
      >
        <p className="text-sm text-neutral-500">Posti assegnati nella scheda "Tavoli". Puoi anche farlo seguire l'orario della giornata:</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label-wed">Sblocca dalle</label>
            <input className="input-wed" type="datetime-local" value={form.tables_unlock_at} onChange={(e) => setForm({ ...form, tables_unlock_at: e.target.value })} data-testid="tables-unlock-input" />
            <p className="text-xs text-neutral-400 mt-1">Vuoto = inizio matrimonio.</p>
          </div>
          <div>
            <label className="label-wed">Nascondi (con Programma) dalle</label>
            <input className="input-wed" type="datetime-local" value={form.tables_hide_at} onChange={(e) => setForm({ ...form, tables_hide_at: e.target.value })} data-testid="tables-hide-input" />
            <p className="text-xs text-neutral-400 mt-1">Vuoto = mai.</p>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="Domande frequenti"
        enabled={form.sections_enabled.faq !== false}
        onToggle={(v) => toggleSection("faq", v)}
        testId="faq-settings-card"
      >
        <div className="flex flex-wrap gap-2">
          {FAQ_SUGGESTIONS.filter((s) => !form.faq.some((f) => f.domanda === s)).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => addFaq(s)}
              className="text-xs bg-[#faf6ee] border rounded-full px-3 py-1.5 hover:border-[var(--wed-primary)] transition-colors duration-150"
              style={{ borderColor: "#e3ddd2" }}
              data-testid={`faq-suggestion-${s.slice(0, 10)}`}
            >
              <Plus size={11} className="inline -mt-0.5 mr-1" />{s}
            </button>
          ))}
        </div>
        <div className="space-y-3">
          {form.faq.map((f, i) => (
            <div key={f.id} className="border rounded-xl p-3 space-y-2" style={{ borderColor: "#eee8dd" }} data-testid={`faq-form-item-${i}`}>
              <div className="flex items-center gap-2">
                <input className="input-wed !py-2 flex-1" value={f.domanda} onChange={(e) => updateFaq(i, "domanda", e.target.value)} placeholder="Domanda" data-testid={`faq-question-${i}`} />
                <button type="button" onClick={() => removeFaq(i)} className="p-2 text-neutral-400 hover:text-rose-500 transition-colors duration-150" data-testid={`faq-remove-${i}`}><Trash2 size={15} /></button>
              </div>
              <textarea className="input-wed !py-2 text-sm" rows={2} value={f.risposta} onChange={(e) => updateFaq(i, "risposta", e.target.value)} placeholder="Risposta" data-testid={`faq-answer-${i}`} />
            </div>
          ))}
          {form.faq.length === 0 && <p className="text-sm text-neutral-400">Nessuna domanda ancora.</p>}
        </div>
        <button type="button" onClick={() => addFaq()} className="btn-outline-wed !py-2 !px-4 text-sm flex items-center gap-2" data-testid="faq-add-button">
          <Plus size={15} /> Aggiungi domanda
        </button>
      </SectionCard>

      <SectionCard
        title="Lista nozze / regalo"
        enabled={form.gift_enabled}
        onToggle={(v) => setForm({ ...form, gift_enabled: v })}
        testId="gift-settings-card"
      >
        <div>
          <label className="label-wed">Messaggio per gli invitati</label>
          <textarea className="input-wed" rows={3} value={form.gift_message} onChange={(e) => setForm({ ...form, gift_message: e.target.value })} placeholder="Es. La vostra presenza è il regalo più bello…" data-testid="gift-message-input" />
        </div>
        <div>
          <label className="label-wed">IBAN (facoltativo)</label>
          <input className="input-wed font-mono" value={form.gift_iban} onChange={(e) => setForm({ ...form, gift_iban: e.target.value })} placeholder="IT00 X000 0000..." data-testid="gift-iban-input" />
        </div>
      </SectionCard>

      <SectionCard
        title={<span className="flex items-center gap-2"><Music size={18} style={{ color: "var(--wed-primary)" }} />Richieste musicali</span>}
        enabled={form.songs_enabled}
        onToggle={(v) => setForm({ ...form, songs_enabled: v })}
        testId="songs-settings-card"
      >
        <p className="text-sm text-neutral-500">Gli invitati suggeriscono canzoni per la playlist.</p>
        {songs.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-2 border-t" style={{ borderColor: "#f0ece1" }} data-testid="songs-list">
            {songs.map((s) => (
              <span key={s.id} className="flex items-center gap-2 text-xs bg-neutral-50 border rounded-full pl-1.5 pr-1.5 py-1.5" style={{ borderColor: "#e3ddd2" }}>
                {s.album_image_url ? (
                  <img src={s.album_image_url} alt="" className="w-6 h-6 rounded-full object-cover" />
                ) : (
                  <span className="text-sm">🎵</span>
                )}
                {s.canzone}{s.artista ? ` — ${s.artista}` : ""}{s.nome ? ` (${s.nome})` : ""}
                <button type="button" onClick={() => removeSong(s.id)} className="text-neutral-400 hover:text-rose-500 transition-colors duration-150" data-testid={`song-remove-${s.id}`}><Trash2 size={12} /></button>
              </span>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard
        title="Modulo RSVP"
        enabled={form.rsvp_enabled}
        onToggle={(v) => setForm({ ...form, rsvp_enabled: v })}
        testId="rsvp-settings-card"
      >
        <div className="max-w-xs">
          <label className="label-wed">Nascondilo (giorni prima dell'evento)</label>
          <input
            className="input-wed"
            type="number"
            min={0}
            value={form.rsvp_cutoff_days}
            onChange={(e) => setForm({ ...form, rsvp_cutoff_days: Math.max(0, Number(e.target.value) || 0) })}
            data-testid="rsvp-cutoff-input"
          />
          <p className="text-xs text-neutral-400 mt-1">Es. 7 = scompare una settimana prima. 0 = resta fino al giorno stesso.</p>
        </div>
        <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer pt-1 border-t" style={{ borderColor: "#f0ece1" }}>
          <input type="checkbox" checked={form.rsvp_ask_moments} onChange={(e) => setForm({ ...form, rsvp_ask_moments: e.target.checked })} data-testid="rsvp-ask-moments-toggle" />
          Chiedi anche a quali momenti parteciperanno
        </label>
      </SectionCard>

      <div className="bg-white border rounded-2xl p-6 space-y-4" style={{ borderColor: "#e3ddd2" }}>
        <h2 className="font-wed text-xl">Tema pagina pubblica</h2>
        <div>
          <label className="label-wed">Colore primario</label>
          <div className="flex items-center gap-3 flex-wrap">
            {PRESETS.map((c) => (
              <button key={c} type="button" onClick={() => setForm({ ...form, primary: c })} className={`w-9 h-9 rounded-full border-2 transition-transform duration-150 hover:scale-110 ${form.primary === c ? "ring-2 ring-offset-2" : ""}`} style={{ background: c, borderColor: "#fff" }} data-testid={`settings-color-${c.slice(1)}`} />
            ))}
            <input type="color" value={form.primary} onChange={(e) => setForm({ ...form, primary: e.target.value })} className="w-9 h-9 rounded cursor-pointer border-0" data-testid="settings-color-picker" />
          </div>
        </div>
        <div>
          <label className="label-wed">Font titoli</label>
          <select className="input-wed" value={form.font} onChange={(e) => setForm({ ...form, font: e.target.value })} data-testid="settings-font-select">
            {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div className="rounded-xl p-5 border" style={{ borderColor: "#eee8dd" }}>
          <p className="text-xs text-neutral-400 mb-2">Anteprima</p>
          <p className="text-3xl" style={{ fontFamily: `'${form.font}', serif`, color: form.primary }}>{form.nome_sposi}</p>
        </div>
      </div>

      <div className="bg-white border rounded-2xl p-6 space-y-4" style={{ borderColor: "#e3ddd2" }} data-testid="moments-card">
        <h2 className="font-wed text-xl">Momenti dell'evento</h2>
        <p className="text-sm text-neutral-500">Fasce a cui un invitato può partecipare (es. Pranzo, Taglio torta).</p>
        <div className="space-y-2">
          {form.moments.map((m, i) => (
            <div key={m.id} className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
              <input
                className="input-wed flex-1 min-w-[140px]"
                value={m.label}
                onChange={(e) => updateMoment(i, e.target.value)}
                placeholder="Es. Pranzo"
                data-testid={`moment-input-${i}`}
              />
              <label className="flex items-center gap-1.5 text-xs text-neutral-500 whitespace-nowrap cursor-pointer" data-testid={`moment-seat-toggle-${i}`}>
                <input type="checkbox" checked={m.requiresSeat !== false} onChange={(e) => toggleMomentSeat(i, e.target.checked)} />
                Richiede posto a tavola
              </label>
              <button type="button" onClick={() => removeMoment(i)} className="p-2 text-neutral-400 hover:text-rose-500 transition-colors duration-150" data-testid={`moment-remove-${i}`}>
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          {form.moments.length === 0 && <p className="text-sm text-neutral-400">Nessun momento definito ancora.</p>}
          <p className="text-xs text-neutral-400 pt-1">Solo i momenti "richiede posto" compaiono nel Designer tavoli.</p>
        </div>
        <button type="button" onClick={addMoment} className="btn-outline-wed !py-2 !px-4 text-sm flex items-center gap-2" data-testid="moment-add-button">
          <Plus size={15} /> Aggiungi momento
        </button>
      </div>

      <div className="bg-white border rounded-2xl p-6 space-y-4" style={{ borderColor: "#e3ddd2" }} data-testid="qr-card">
        <div className="flex items-center gap-2">
          <QrCode size={18} style={{ color: "var(--wed-primary)" }} />
          <h2 className="font-wed text-xl">QR code invito</h2>
        </div>
        <p className="text-sm text-neutral-500">Stampa questo QR code sugli inviti: gli ospiti lo scansionano per aprire la pagina evento.</p>
        <div className="flex flex-col sm:flex-row items-center gap-6">
          {qr && <img src={qr} alt="QR code evento" className="w-40 h-40 rounded-xl border" style={{ borderColor: "#eee8dd" }} data-testid="qr-image" />}
          <div className="flex-1 w-full space-y-3">
            <div className="text-sm text-neutral-500 break-all bg-neutral-50 rounded-lg px-3 py-2" data-testid="qr-public-url">{publicUrl}</div>
            <div className="flex gap-2 flex-wrap">
              <button type="button" onClick={downloadQr} className="btn-wed !py-2 !px-4 text-sm flex items-center gap-2" data-testid="qr-download-button">
                <Download size={15} /> Scarica QR
              </button>
              <button type="button" onClick={copyLink} className="btn-outline-wed !py-2 !px-4 text-sm flex items-center gap-2" data-testid="qr-copy-button">
                <Copy size={15} /> Copia link
              </button>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t space-y-3" style={{ borderColor: "#f0ece1" }}>
          <p className="text-sm font-medium">Stampa un cartoncino per il matrimonio</p>
          <p className="text-xs text-neutral-500">Da mostrare su ogni tavolo o all'ingresso. Scegli la scritta, o scrivine una tua.</p>
          <div className="flex flex-wrap gap-2">
            {QR_CAPTION_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => setQrCaption(preset)}
                className="text-xs rounded-full px-3 py-1.5 border transition-colors duration-150"
                style={
                  qrCaption === preset
                    ? { background: "var(--wed-primary)", borderColor: "var(--wed-primary)", color: "#fff" }
                    : { borderColor: "#e3ddd2", color: "#6b6459" }
                }
                data-testid={`qr-caption-preset-${QR_CAPTION_PRESETS.indexOf(preset)}`}
              >
                {preset}
              </button>
            ))}
          </div>
          <input
            className="input-wed text-sm"
            value={qrCaption}
            onChange={(e) => setQrCaption(e.target.value)}
            placeholder="…oppure scrivi una scritta personalizzata"
            data-testid="qr-caption-input"
          />
          <button
            type="button"
            onClick={() => setShowQrPrint(true)}
            disabled={!qr}
            className="btn-outline-wed !py-2 !px-4 text-sm flex items-center gap-2"
            data-testid="qr-print-button"
          >
            <Printer size={15} /> Stampa cartoncino
          </button>
        </div>
      </div>

      {showQrPrint && <QrCardPrint wedding={wedding} qr={qr} caption={qrCaption} onClose={() => setShowQrPrint(false)} />}

      <CollaboratorsCard wedding={wedding} />

      <DangerZoneCard wedding={wedding} />

      <button type="submit" className="btn-wed" disabled={saving} data-testid="settings-save-button">
        {saving ? "Salvataggio…" : "Salva modifiche"}
      </button>
    </form>
  );
}

function CollaboratorsCard({ wedding }) {
  const [collaborators, setCollaborators] = useState([]);
  const [email, setEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [lastLink, setLastLink] = useState(null);

  const load = () => api.get("/owner/collaborators").then((r) => setCollaborators(r.data)).catch(() => {});
  useEffect(() => { load(); }, [wedding.id]);

  const invite = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return toast.error("Inserisci un'email");
    setInviting(true);
    try {
      const { data } = await api.post("/owner/collaborators", { email: email.trim() });
      const link = `${window.location.origin}/invite/${data.invite_token}`;
      setLastLink(link);
      setEmail("");
      load();
      toast.success("Invito creato — copia e invia il link qui sotto");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nell'invito");
    } finally {
      setInviting(false);
    }
  };

  const removeCollaborator = async (id) => {
    try {
      await api.delete(`/owner/collaborators/${id}`);
      setCollaborators((cs) => cs.filter((c) => c.id !== id));
      toast.success("Accesso revocato");
    } catch {
      toast.error("Errore nella rimozione");
    }
  };

  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copiato!");
    } catch {
      toast.error("Impossibile copiare il link");
    }
  };

  return (
    <div className="bg-white border rounded-2xl p-6 space-y-4" style={{ borderColor: "#e3ddd2" }} data-testid="collaborators-card">
      <div className="flex items-center gap-2">
        <Users size={18} style={{ color: "var(--wed-primary)" }} />
        <h2 className="font-wed text-xl">Collabora con qualcuno</h2>
      </div>
      <p className="text-sm text-neutral-500">
        {wedding.is_owner === false
          ? "Stai collaborando su questo matrimonio."
          : "Dai accesso alla dashboard al tuo partner o al wedding planner. Nessuna email viene inviata automaticamente: genera il link e inviaglielo tu (WhatsApp, email, ecc.)."}
      </p>

      {wedding.is_owner !== false && (
        <div className="flex gap-2">
          <input
            className="input-wed flex-1"
            type="email"
            placeholder="email@esempio.it"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); invite(e); } }}
            data-testid="collaborator-email-input"
          />
          <button type="button" onClick={invite} disabled={inviting} className="btn-wed !px-4 text-sm shrink-0" data-testid="collaborator-invite-button">
            {inviting ? "…" : "Invita"}
          </button>
        </div>
      )}

      {lastLink && (
        <div className="bg-[#faf6ee] border rounded-xl p-3 flex items-center gap-2" style={{ borderColor: "#e3ddd2" }} data-testid="collaborator-invite-link">
          <p className="text-xs text-neutral-600 break-all flex-1">{lastLink}</p>
          <button type="button" onClick={() => copyLink(lastLink)} className="btn-outline-wed !py-1.5 !px-3 text-xs shrink-0" data-testid="collaborator-copy-link-button">
            <Copy size={13} />
          </button>
        </div>
      )}

      {collaborators.length > 0 && (
        <div className="space-y-2 pt-2 border-t" style={{ borderColor: "#f0ece1" }}>
          {collaborators.map((c) => (
            <div key={c.id} className="flex items-center justify-between text-sm" data-testid={`collaborator-row-${c.id}`}>
              <span>{c.email}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-0.5 rounded-full" style={c.status === "accepted" ? { background: "#e8f0e8", color: "#4a7a4a" } : { background: "#f5f3ec", color: "#9a9a9a" }}>
                  {c.status === "accepted" ? "Attivo" : "In attesa"}
                </span>
                {wedding.is_owner !== false && (
                  <button type="button" onClick={() => removeCollaborator(c.id)} className="text-neutral-400 hover:text-rose-500 transition-colors duration-150" data-testid={`collaborator-remove-${c.id}`}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DangerZoneCard({ wedding }) {
  const { user } = useAuth();
  const [weddingConfirm, setWeddingConfirm] = useState("");
  const [accountConfirm, setAccountConfirm] = useState("");
  const [deletingWedding, setDeletingWedding] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  const deleteWedding = async () => {
    if (weddingConfirm.trim().toLowerCase() !== wedding.slug) {
      return toast.error("Lo slug digitato non corrisponde");
    }
    setDeletingWedding(true);
    try {
      await api.delete("/owner/wedding");
      toast.success("Matrimonio eliminato");
      window.location.href = "/dashboard";
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nell'eliminazione");
      setDeletingWedding(false);
    }
  };

  const deleteAccount = async () => {
    if (accountConfirm.trim().toLowerCase() !== (user?.email || "").toLowerCase()) {
      return toast.error("L'email digitata non corrisponde");
    }
    setDeletingAccount(true);
    try {
      await api.delete("/auth/account");
      localStorage.removeItem("access_token");
      toast.success("Account eliminato");
      window.location.href = "/";
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nell'eliminazione");
      setDeletingAccount(false);
    }
  };

  return (
    <div className="bg-white border border-rose-200 rounded-2xl p-6 space-y-6" data-testid="danger-zone-card">
      <div className="flex items-center gap-2">
        <AlertTriangle size={18} className="text-rose-500" />
        <h2 className="font-wed text-xl text-rose-600">Zona pericolosa</h2>
      </div>

      {wedding.is_owner !== false && (
        <div className="space-y-2 pb-6 border-b border-rose-100">
          <p className="text-sm font-medium">Elimina questo matrimonio</p>
          <p className="text-xs text-neutral-500">
            Cancella definitivamente invitati, tavoli, programma, budget, foto e video di "{wedding.nome_sposi}". Non è reversibile.
          </p>
          <div className="flex gap-2 pt-1">
            <input
              className="input-wed flex-1 !border-rose-200"
              placeholder={`Digita "${wedding.slug}" per confermare`}
              value={weddingConfirm}
              onChange={(e) => setWeddingConfirm(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
              data-testid="delete-wedding-confirm-input"
            />
            <button
              type="button"
              onClick={deleteWedding}
              disabled={deletingWedding || weddingConfirm.trim().toLowerCase() !== wedding.slug}
              className="shrink-0 rounded-full px-5 text-sm font-medium text-white bg-rose-500 hover:bg-rose-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
              data-testid="delete-wedding-button"
            >
              {deletingWedding ? "…" : "Elimina matrimonio"}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <p className="text-sm font-medium">Elimina il tuo account</p>
        <p className="text-xs text-neutral-500">
          Cancella il tuo account e tutti i matrimoni di cui sei organizzatore. Se collabori su matrimoni di altri, il tuo accesso lì viene semplicemente rimosso. Non è reversibile.
        </p>
        <div className="flex gap-2 pt-1">
          <input
            className="input-wed flex-1 !border-rose-200"
            type="email"
            placeholder={`Digita "${user?.email || ""}" per confermare`}
            value={accountConfirm}
            onChange={(e) => setAccountConfirm(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
            data-testid="delete-account-confirm-input"
          />
          <button
            type="button"
            onClick={deleteAccount}
            disabled={deletingAccount || accountConfirm.trim().toLowerCase() !== (user?.email || "").toLowerCase()}
            className="shrink-0 rounded-full px-5 text-sm font-medium text-white bg-rose-500 hover:bg-rose-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
            data-testid="delete-account-button"
          >
            {deletingAccount ? "…" : "Elimina account"}
          </button>
        </div>
      </div>
    </div>
  );
}
